import { gql } from '@apollo/client';
import { SERVICE_CATALOG_FIELDS } from '@/graphql/fragments/services';

export const SERVICE_CATALOG_QUERY = gql`
    ${SERVICE_CATALOG_FIELDS}

    query ServiceCatalog($where: service_catalog_bool_exp!) {
        service_catalog(where: $where, order_by: { service_code: asc }) {
            ...ServiceCatalogFields
        }
    }
`;

const clean = (value) => {
    if (value === null || value === undefined) return '';
    return `${value}`.trim();
};

const dimensionCount = (row) =>
    row?.dimensions && typeof row.dimensions === 'object' ? Object.keys(row.dimensions).length : 0;

// Empty scope -> omit the filter (read the whole shipper catalog).
export const buildServiceCatalogWhere = ({ shipperId, orderScope } = {}) => {
    const where = { client_id: { _eq: shipperId } };
    if (orderScope && typeof orderScope === 'object' && Object.keys(orderScope).length > 0) {
        where.dimensions = { _contained_in: orderScope };
    }
    return where;
};

// A service_catalog row -> the option shape the pickers and line builders read. Exported for
// mapping a catalog write's response the same way.
export const buildServiceOption = (row) => {
    const code = clean(row?.service_code);
    if (!code) return null;

    const type = clean(row.service_type).toLowerCase();
    const size = clean(row.size);
    return {
        service_id: row.service_id,
        code,
        description: clean(row.description) || code,
        type: type || 'unknown',
        ...(size ? { size } : {}),
        require_confirm: !!row.require_confirm,
        // Catalog rows default billable=true; only a real false is meaningful here.
        billable: row.billable !== false,
        ...(Number.isFinite(row.service_time) ? { service_time: row.service_time } : {}),
    };
};

// One row per service_code, keeping the most-specific descriptor (site over global).
const mostSpecificByCode = (rows = []) => {
    const byCode = new Map();
    rows.forEach((row) => {
        const code = clean(row?.service_code);
        if (!code) return;
        const existing = byCode.get(code);
        if (!existing) {
            byCode.set(code, row);
            return;
        }
        const delta = dimensionCount(row) - dimensionCount(existing);
        if (delta > 0 || (delta === 0 && clean(row.dimension_key) < clean(existing.dimension_key))) {
            byCode.set(code, row);
        }
    });
    return Array.from(byCode.values());
};

// `fetchPolicy` is for re-reading after a catalog write: the write's response reaches Apollo's
// normalized cache but not this query's cached list, so a cache-first re-read stays stale.
export const fetchServiceCatalog = async (client, { shipperId, orderScope } = {}, { fetchPolicy } = {}) => {
    const { data } = await client.query({
        query: SERVICE_CATALOG_QUERY,
        variables: { where: buildServiceCatalogWhere({ shipperId, orderScope }) },
        ...(fetchPolicy ? { fetchPolicy } : {}),
    });

    const services = mostSpecificByCode(data?.service_catalog || [])
        .map(buildServiceOption)
        .filter(Boolean)
        .sort((a, b) => a.code.localeCompare(b.code));

    return { shipper_id: shipperId, services };
};
